import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Menu, Space, Spin, message } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faFileExport, faBan } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
// UTILS
import { downloadExport } from "../../../utils/exportDownload";
// INTERFACES
import { RootState } from "../../../app/store";
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";

/** Menu label of the draft row's export action. */
export const DRAFT_EXPORT_LABEL = "Export (LNX)";

/**
 * The ⋯ menu at the right of a draft's row. Export downloads the whole draft as
 * one binary export — every wizard stage, the unsaved wizard steps included —
 * from GET /export/full-org/:orgId, the same file the Organizations table's
 * export and the setup wizard's Review step give.
 */
export default function OrganizationDraftsTableActions({
  selectedItem,
}: {
  selectedItem: OrganizationInterface;
}) {
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport({
        orgId: formatUuidT(selectedItem.id),
        includeDeps: true,
        entityName: `${selectedItem.name}_full`,
      });
      message.success("Export downloaded successfully");
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Exporting an organization is gated as it is on the Organizations table.
  const items = hasPermission(Permission.OrgsView, permissionSet ?? [])
    ? [
        {
          label: (
            <TableActionsMenuItem
              icon={faFileExport}
              text={DRAFT_EXPORT_LABEL}
              onClick={handleExport}
            />
          ),
          key: "export",
        },
      ]
    : [
        {
          label: (
            <span>
              <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
              No actions
            </span>
          ),
          key: "none",
        },
      ];

  return (
    <Dropdown overlay={<Menu items={items} />} trigger={["click"]} disabled={exporting}>
      <Space className="generalStyles__hoverCursor" aria-label="Draft actions" role="button">
        {exporting ? <Spin size="small" /> : <FontAwesomeIcon icon={faEllipsis} />}
      </Space>
    </Dropdown>
  );
}
